import { PAYMENT_METHODS, santimToEtb, etbToSantim, type AppConfigDto, type PaymentMethod } from '@bravo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, ErrorBlock, Field, PageHeader, Select, Spinner, TextArea, TextInput } from '../components/ui';
import { apiData } from '../lib/api';
import { toastError, toastOk } from '../lib/feedback';
import { useFormat } from '../lib/format';

interface AccountDraft {
  method: PaymentMethod;
  accountName: string;
  accountNumber: string;
  en: string;
  am: string;
}

interface FormState {
  feeBps: string;
  minStake: string;
  maxStake: string;
  maxUserPerMarket: string;
  minTopUp: string;
  minWithdrawal: string;
  maxPendingWithdrawals: string;
  largeTopUpThreshold: string;
  largeWithdrawalThreshold: string;
  minTurnoverRatio: string;
  requireTwoPersonResolution: boolean;
  accounts: AccountDraft[];
}

function fromConfig(config: AppConfigDto): FormState {
  return {
    feeBps: String(config.feeBps),
    minStake: santimToEtb(config.minStake),
    maxStake: santimToEtb(config.maxStake),
    maxUserPerMarket: santimToEtb(config.maxUserPerMarket),
    minTopUp: santimToEtb(config.minTopUp),
    minWithdrawal: santimToEtb(config.minWithdrawal),
    maxPendingWithdrawals: String(config.maxPendingWithdrawals),
    largeTopUpThreshold: santimToEtb(config.largeTopUpThreshold),
    largeWithdrawalThreshold: santimToEtb(config.largeWithdrawalThreshold),
    minTurnoverRatio: String(config.minTurnoverRatio),
    requireTwoPersonResolution: config.requireTwoPersonResolution,
    accounts: config.depositAccounts.map((account) => ({
      method: account.method,
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      en: account.instructions.en,
      am: account.instructions.am,
    })),
  };
}

export function ConfigPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const query = useQuery({
    queryKey: ['config'],
    queryFn: () => apiData<AppConfigDto>('/admin/config'),
  });

  useEffect(() => {
    if (query.data) setForm(fromConfig(query.data));
  }, [query.data]);

  if (query.isLoading || !form) return <Spinner />;
  if (query.isError) return <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} />;

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current));
  }

  async function save() {
    setBusy(true);
    try {
      await apiData('/admin/config', {
        method: 'PATCH',
        body: {
          feeBps: Number(form?.feeBps),
          minStake: etbToSantim(form?.minStake ?? ''),
          maxStake: etbToSantim(form?.maxStake ?? ''),
          maxUserPerMarket: etbToSantim(form?.maxUserPerMarket ?? ''),
          minTopUp: etbToSantim(form?.minTopUp ?? ''),
          minWithdrawal: etbToSantim(form?.minWithdrawal ?? ''),
          maxPendingWithdrawals: Number(form?.maxPendingWithdrawals),
          largeTopUpThreshold: etbToSantim(form?.largeTopUpThreshold ?? ''),
          largeWithdrawalThreshold: etbToSantim(form?.largeWithdrawalThreshold ?? ''),
          minTurnoverRatio: Number(form?.minTurnoverRatio),
          requireTwoPersonResolution: form?.requireTwoPersonResolution,
          depositAccounts: form?.accounts.map((account) => ({
            method: account.method,
            accountName: account.accountName,
            accountNumber: account.accountNumber,
            instructions: { en: account.en, am: account.am },
          })),
        },
      });
      toastOk(t('saved'));
      await queryClient.invalidateQueries({ queryKey: ['config'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  const money: Array<keyof FormState> = [
    'minStake',
    'maxStake',
    'maxUserPerMarket',
    'minTopUp',
    'minWithdrawal',
    'largeTopUpThreshold',
    'largeWithdrawalThreshold',
  ];

  return (
    <div>
      <PageHeader title={t('config.title')} subtitle={t('config.subtitle')} />
      <Card className="grid gap-3 md:grid-cols-2">
        <Field label={t('config.feeBps')}>
          <TextInput value={form.feeBps} onChange={(event) => patch('feeBps', event.target.value)} />
        </Field>
        {money.map((key) => (
          <Field key={key} label={t(`config.${key}`)} hint={t('users.etbHint')}>
            <TextInput
              value={String(form[key])}
              onChange={(event) => patch(key, event.target.value)}
            />
          </Field>
        ))}
        <Field label={t('config.maxPendingWithdrawals')}>
          <TextInput
            value={form.maxPendingWithdrawals}
            onChange={(event) => patch('maxPendingWithdrawals', event.target.value)}
          />
        </Field>
        <Field label={t('config.minTurnoverRatio')}>
          <TextInput
            value={form.minTurnoverRatio}
            onChange={(event) => patch('minTurnoverRatio', event.target.value)}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm md:col-span-2">
          <input
            type="checkbox"
            checked={form.requireTwoPersonResolution}
            onChange={(event) => patch('requireTwoPersonResolution', event.target.checked)}
          />
          {t('config.twoPerson')}
        </label>
      </Card>

      <h2 className="mb-3 mt-8 text-lg font-semibold">{t('config.accounts')}</h2>
      <div className="space-y-3">
        {form.accounts.map((account, index) => (
          <Card key={index} className="grid gap-3 md:grid-cols-2">
            <Field label={t('method')}>
              <Select
                value={account.method}
                onChange={(event) => {
                  const accounts = form.accounts.slice();
                  const current = accounts[index];
                  if (!current) return;
                  accounts[index] = { ...current, method: event.target.value as PaymentMethod };
                  patch('accounts', accounts);
                }}
              >
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {format.method(method)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('config.accountName')}>
              <TextInput
                value={account.accountName}
                onChange={(event) => {
                  const accounts = form.accounts.slice();
                  const current = accounts[index];
                  if (!current) return;
                  accounts[index] = { ...current, accountName: event.target.value };
                  patch('accounts', accounts);
                }}
              />
            </Field>
            <Field label={t('config.accountNumber')}>
              <TextInput
                value={account.accountNumber}
                onChange={(event) => {
                  const accounts = form.accounts.slice();
                  const current = accounts[index];
                  if (!current) return;
                  accounts[index] = { ...current, accountNumber: event.target.value };
                  patch('accounts', accounts);
                }}
              />
            </Field>
            <Field label={t('markets.sourceEn')}>
              <TextArea
                value={account.en}
                onChange={(event) => {
                  const accounts = form.accounts.slice();
                  const current = accounts[index];
                  if (!current) return;
                  accounts[index] = { ...current, en: event.target.value };
                  patch('accounts', accounts);
                }}
              />
            </Field>
            <Field label={t('markets.sourceAm')}>
              <TextArea
                value={account.am}
                onChange={(event) => {
                  const accounts = form.accounts.slice();
                  const current = accounts[index];
                  if (!current) return;
                  accounts[index] = { ...current, am: event.target.value };
                  patch('accounts', accounts);
                }}
              />
            </Field>
            <Button
              variant="ghost"
              onClick={() => patch('accounts', form.accounts.filter((_, i) => i !== index))}
            >
              {t('markets.remove')}
            </Button>
          </Card>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <Button
          variant="secondary"
          onClick={() =>
            patch('accounts', [
              ...form.accounts,
              { method: 'telebirr', accountName: '', accountNumber: '', en: '', am: '' },
            ])
          }
        >
          {t('config.addAccount')}
        </Button>
        <Button loading={busy} onClick={() => void save()}>
          {t('save')}
        </Button>
      </div>
    </div>
  );
}
